import Logger from '@utils/log'
import { useCallback } from 'react'
import { useWire } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { PURCHASE_TYPE_VIEW_INDEX_DOCUMENT, hasNoFile } from '@constants'
import useNavigate from '@hook/useNavigate'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import usePayments from '@hook/usePayments'

const log = new Logger('useDocumentPopupHandler')

/**
 * Helper utilities for launching the document popup viewer
 * @returns {{popupDocumentOrNavigate: (function(*, boolean=): function(*): Promise<void|undefined>)}}
 */
const useDocumentPopupHandler = () => {
    
    const navigate = useNavigate()
    const [, setSearchParams] = useSearchParams()
    
    const documentViewerModalDocument = useWire(store.documentViewerModalDocument)
    const blinkAvatarIndicator = useWire(store.blinkAvatarIndicator)
    
    const payments = usePayments()
    
    const showDocumentDialogOrNavigate = (record, updateSearchParams, shouldNavigate) => {
        
        actions.addDocumentToHistory(record)
        
        if (shouldNavigate) return navigate.toViewDocumentId(record.id)
        
        if (updateSearchParams)
            setSearchParams(prev => {
                prev.set('documentId', record.id)
                return prev
            })
        
        documentViewerModalDocument.setValue(record)
        
    }
    
    /**
     * Opens the document popup viewer
     * Prompts the user for payment first, if necessary
     * 
     * @param {Record} record
     * @param {PurchaseType} purchaseType
     * @param {Boolean} [updateSearchParams=true] - If the browser's query string should be updated with the document ID
     * @returns {(function(*): Promise<void>)}
     */
    const popupDocumentOrNavigate = useCallback((
        record,
        purchaseType = PURCHASE_TYPE_VIEW_INDEX_DOCUMENT,
        updateSearchParams = true,
        shouldNavigate = false,
    ) => async e => {
        
        log.d('popupDocumentOrNavigate', { record, updateSearchParams })
        
        e.preventDefault()
        
        if (hasNoFile(record))
            return console.warn('hasNoFile', record)
        
        if (payments.shouldShowPurchaseRecordDialog(record, purchaseType, 1)) {
            
            return payments.showPurchaseDialog({
                purchaseType,
                itemCount: 1,
                onConfirmPayment: async (remainingBalance, fundingSource) => {
                    try {
                        await actions.purchaseDocument(record.id, purchaseType, fundingSource)
                        actions.updateDocumentPurchasedStatus(record.id)
                        showDocumentDialogOrNavigate(record, updateSearchParams, shouldNavigate)
                    } catch (e) {
                        console.error('useDocumentPopupHandler#onConfirm', e)
                        toast.error('Failed to purchase document')
                    }
                },
            })
            
        } else if (record.collection?.isParse && payments.remoteConfig?.payments?.enabled) {
            
            // User has likely said "don't remind me" in the past
            // so try to passively purchase the document
            if (!payments.userOrOrgCanAffordPurchase(1, purchaseType)) {
                toast.warn('Your balance is too low to purchase this document')
                blinkAvatarIndicator.setValue(true)
                return
            }
            
            try {
                await actions.purchaseDocument(record.id, purchaseType, payments.fundingSource)
                actions.updateDocumentPurchasedStatus(record.id)
                
            } catch (e) {
                console.error('popupDocumentOrNavigate', e)
                toast.error('Failed to purchase document')
                return
            }
        }
        
        showDocumentDialogOrNavigate(record, updateSearchParams, shouldNavigate)
        
    }, [
        documentViewerModalDocument,
        navigate,
        setSearchParams,
    ])
    
    return {
        popupDocumentOrNavigate,
        payments,
    }
    
}

export default useDocumentPopupHandler
